import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface ModalConfig {
  id: string;
  component: React.ComponentType<any>;
  props?: Record<string, any>;
  options?: {
    size?: 'small' | 'medium' | 'large' | 'fullscreen';
    closable?: boolean;
    backdrop?: 'blur' | 'dark' | 'none';
    position?: 'center' | 'top' | 'bottom';
    animation?: 'fade' | 'slide' | 'scale' | 'bounce';
    zIndex?: number;
    disableSidebarOffset?: boolean;
  };
}

interface ModalContextType {
  modals: ModalConfig[];
  openModal: (config: Omit<ModalConfig, 'id'> & { id?: string }) => string;
  closeModal: (id: string) => void;
  closeAllModals: () => void;
  updateModal: (id: string, updates: Partial<ModalConfig>) => void;
  isModalOpen: (id: string) => boolean;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [modals, setModals] = useState<ModalConfig[]>([]);

  const openModal = useCallback((config: Omit<ModalConfig, 'id'> & { id?: string }): string => {
    const id = config.id || `modal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const modalConfig: ModalConfig = {
      ...config,
      id,
      options: {
        size: 'medium',
        closable: true,
        backdrop: 'blur',
        position: 'center',
        animation: 'fade',
        zIndex: 1000,
        ...config.options,
      },
    };

    setModals([modalConfig]);
    return id;
  }, []);

  const closeModal = useCallback((id: string) => {
    setModals(prev => prev.filter(modal => modal.id !== id));
  }, []);

  const closeAllModals = useCallback(() => {
    setModals([]);
  }, []);

  const updateModal = useCallback((id: string, updates: Partial<ModalConfig>) => {
    setModals(prev =>
      prev.map(modal =>
        modal.id === id ? { ...modal, ...updates } : modal
      )
    );
  }, []);

  const isModalOpen = useCallback((id: string) => {
    return modals.some(modal => modal.id === id);
  }, [modals]);

  const value: ModalContextType = {
    modals,
    openModal,
    closeModal,
    closeAllModals,
    updateModal,
    isModalOpen,
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
    </ModalContext.Provider>
  );
};